import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/* eslint-disable no-plusplus */
import React, { useMemo, useState, useEffect, useRef, forwardRef, useImperativeHandle, useContext } from 'react';
import clsx from 'clsx';
import { CSSTransition } from 'react-transition-group';
import Icon from '../icon';
import Overlay from '../overlay';
import useEventListener from '../hooks/use-event-listener';
import { isDef } from '../utils';
import { callInterceptor } from '../utils/interceptor';
import { renderToContainer } from '../utils/dom/renderToContainer';
import useSsrCompat from '../hooks/use-ssr-compat';
import ConfigProviderContext from '../config-provider/ConfigProviderContext';
import PopupContext from './PopupContext';
export var sharedPopupProps = ['round', 'zIndex', 'closeable', 'overlay', 'overlayClass', 'overlayStyle', 'destroyOnClose', 'forceRender', 'lockScroll', 'duration', 'transition', 'closeOnClickOverlay', 'closeOnPopstate', 'onClickOverlay', 'safeAreaInsetBottom', 'onOpen', 'onClose', 'onOpened', 'onClosed', 'beforeClose'];
var globalZIndex = 2000;
var Popup = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$zIndex;

  var _useContext = useContext(ConfigProviderContext),
      prefixCls = _useContext.prefixCls,
      createNamespace = _useContext.createNamespace;

  var _createNamespace = createNamespace('popup', prefixCls),
      _createNamespace2 = _slicedToArray(_createNamespace, 1),
      bem = _createNamespace2[0];

  var round = props.round,
      visible = props.visible,
      closeable = props.closeable,
      title = props.title,
      descrition = props.descrition,
      children = props.children,
      duration = props.duration,
      closeIcon = props.closeIcon,
      position = props.position;
  var opened = useRef(false);
  var zIndex = useRef((_props$zIndex = props.zIndex) !== null && _props$zIndex !== void 0 ? _props$zIndex : globalZIndex);
  var popupRef = useRef();

  var _useState = useState(visible),
      _useState2 = _slicedToArray(_useState, 2),
      animatedVisible = _useState2[0],
      setAnimatedVisible = _useState2[1];

  var _useSsrCompat = useSsrCompat(),
      _useSsrCompat2 = _slicedToArray(_useSsrCompat, 2),
      ssrCompatRender = _useSsrCompat2[0],
      rendered = _useSsrCompat2[1];

  var style = useMemo(function () {
    var initStyle = _objectSpread({
      zIndex: zIndex.current
    }, props.style);

    if (isDef(props.duration)) {
      var key = props.position === 'center' ? 'animationDuration' : 'transitionDuration';
      initStyle[key] = "".concat(props.duration, "ms");
    }

    return initStyle;
  }, [zIndex.current, props.style, props.duration]);

  var open = function open() {
    var _props$onOpen;

    if (props.zIndex !== undefined) {
      zIndex.current = +props.zIndex;
    } else {
      zIndex.current = globalZIndex++;
    }

    opened.current = true;
    (_props$onOpen = props.onOpen) === null || _props$onOpen === void 0 ? void 0 : _props$onOpen.call(props);
  };

  var close = function close() {
    if (opened.current) {
      callInterceptor({
        interceptor: props.beforeClose,
        args: ['close'],
        done: function done() {
          var _props$onClose;

          opened.current = false;
          (_props$onClose = props.onClose) === null || _props$onClose === void 0 ? void 0 : _props$onClose.call(props);
        }
      });
    }
  };

  var onClickOverlay = function onClickOverlay(event) {
    var _props$onClickOverlay;

    (_props$onClickOverlay = props.onClickOverlay) === null || _props$onClickOverlay === void 0 ? void 0 : _props$onClickOverlay.call(props, event);

    if (props.closeOnClickOverlay) {
      close();
    }
  };

  var renderOverlay = function renderOverlay() {
    if (props.overlay) {
      return /*#__PURE__*/React.createElement(Overlay, {
        visible: visible && rendered,
        lockScroll: props.lockScroll,
        className: props.overlayClass,
        customStyle: props.overlayStyle,
        zIndex: zIndex.current,
        duration: duration,
        onClick: onClickOverlay
      });
    }

    return null;
  };

  var onClickCloseIcon = function onClickCloseIcon(e) {
    if (props.onClickCloseIcon) {
      props.onClickCloseIcon(e);
    }

    close();
  };

  var renderCloseIcon = function renderCloseIcon() {
    if (closeable) {
      var closeIconPosition = props.closeIconPosition;

      if (typeof closeIcon === 'string') {
        return /*#__PURE__*/React.createElement(Icon, {
          name: closeIcon,
          className: clsx(bem('close-icon', closeIconPosition)),
          classPrefix: props.iconPrefix,
          onClick: onClickCloseIcon
        });
      }

      return /*#__PURE__*/React.createElement("div", {
        className: clsx(bem('close-icon', closeIconPosition)),
        onClick: onClickCloseIcon
      }, closeIcon);
    }

    return null;
  };

  var renderTitle = function renderTitle() {
    if (title) {
      return /*#__PURE__*/React.createElement("div", {
        className: clsx(bem('title'))
      }, title);
    }

    return null;
  };

  var renderDescrition = function renderDescrition() {
    if (descrition) {
      return /*#__PURE__*/React.createElement("div", {
        className: clsx(bem('descrition'))
      }, descrition);
    }

    return null;
  };

  var renderPopup = function renderPopup() {
    var safeAreaInsetBottom = props.safeAreaInsetBottom;
    return /*#__PURE__*/React.createElement("div", {
      ref: popupRef,
      style: _objectSpread(_objectSpread({}, style), {}, {
        display: !visible && !animatedVisible ? 'none' : undefined
      }),
      className: clsx(bem(_defineProperty({
        round: round
      }, position, position)), {
        'rv-safe-area-bottom': safeAreaInsetBottom
      }, props.className),
      onClick: props.onClick
    }, renderTitle(), renderDescrition(), children, renderCloseIcon());
  };

  var renderTransition = function renderTransition() {
    var transition = props.transition,
        destroyOnClose = props.destroyOnClose,
        forceRender = props.forceRender;
    var name = position === 'center' ? "".concat(prefixCls, "-fade") : "".concat(prefixCls, "-popup-slide-").concat(position);
    return /*#__PURE__*/React.createElement(CSSTransition, {
      in: visible && rendered
      /**
       * https://github.com/reactjs/react-transition-group/pull/559
       */
      ,
      nodeRef: popupRef,
      timeout: duration,
      classNames: transition || name,
      mountOnEnter: !forceRender,
      unmountOnExit: destroyOnClose,
      onEnter: open,
      onEntered: props.onOpened,
      onExited: function onExited() {
        var _props$onClosed;

        setAnimatedVisible(false);
        (_props$onClosed = props.onClosed) === null || _props$onClosed === void 0 ? void 0 : _props$onClosed.call(props);
      }
    }, renderPopup());
  };

  useEventListener('popstate', function () {
    if (props.closeOnPopstate) {
      close();
    }
  });
  useEffect(function () {
    if (!rendered) return;

    if (visible) {
      setAnimatedVisible(true);
    }
  }, [visible, rendered]);
  useImperativeHandle(ref, function () {
    return {
      popupRef: popupRef
    };
  });
  return ssrCompatRender(function () {
    return renderToContainer(props.teleport, /*#__PURE__*/React.createElement(PopupContext.Provider, {
      value: {
        visible: visible
      }
    }, renderOverlay(), renderTransition()));
  });
});
Popup.defaultProps = {
  duration: 300,
  overlay: true,
  lockScroll: true,
  position: 'center',
  closeIcon: 'cross',
  closeIconPosition: 'top-right',
  closeOnClickOverlay: true,
  teleport: function teleport() {
    return document.body;
  }
};
export default Popup;